import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { PageHeader } from '@/components/common/PageHeader'
import type { Enums } from '@/lib/database.types'
import css from '@/components/common/Common.module.css'

const segments: Enums<'project_segment'>[] = ['residential', 'commercial', 'hotel', 'public_sector', 'industrial', 'other']

export function ProjectCreateForm() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [form, setForm] = useState({
    name: '',
    description: '',
    segment: 'residential' as Enums<'project_segment'>,
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    customer_company: '',
    site_line1: '',
    site_city: '',
    site_postal_code: '',
    site_country: '',
    site_notes: '',
  })

  const update = (field: string, value: string) => setForm(prev => ({ ...prev, [field]: value }))

  const handleSubmit = async () => {
    if (!profile?.organization_id) return
    setLoading(true)
    setError('')

    const { data: refCode } = await supabase.rpc('generate_project_reference')

    const { data, error: err } = await supabase.from('projects').insert({
      organization_id: profile.organization_id,
      created_by: profile.id,
      reference_code: refCode || `PRJ-${Date.now()}`,
      name: form.name,
      description: form.description || null,
      segment: form.segment,
      customer_name: form.customer_name || null,
      customer_email: form.customer_email || null,
      customer_phone: form.customer_phone || null,
      customer_company: form.customer_company || null,
      site_address: form.site_line1 ? {
        line1: form.site_line1,
        city: form.site_city,
        postal_code: form.site_postal_code,
        country: form.site_country,
      } : null,
      site_notes: form.site_notes || null,
    }).select().single()

    if (err) {
      setError(err.message)
      setLoading(false)
      return
    }

    if (data) {
      await supabase.from('activity_log').insert({
        organization_id: profile.organization_id,
        project_id: data.id,
        actor_id: profile.id,
        activity_type: 'project_created',
        entity_type: 'project',
        entity_id: data.id,
        summary: `Project "${form.name}" created`,
      })
      navigate(`/gaviota/projects/${data.id}`)
    }
  }

  return (
    <div>
      <PageHeader title="New Project" description="Create a new commercial engagement" />

      <div className={css.card} style={{ maxWidth: 640 }}>
        {error && <div style={{ background: 'var(--color-error-muted)', color: 'var(--color-error)', padding: 'var(--space-3) var(--space-4)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>{error}</div>}

        {step === 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <h3 style={{ fontSize: 'var(--text-lg)' }}>Project Details</h3>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Project Name *</label>
              <input className={css.formInput} value={form.name} onChange={e => update('name', e.target.value)} placeholder="e.g. Villa Marina Terrace" required />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Segment</label>
              <select className={css.formSelect} value={form.segment} onChange={e => update('segment', e.target.value)}>
                {segments.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
              </select>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Description</label>
              <textarea className={css.formTextarea} value={form.description} onChange={e => update('description', e.target.value)} placeholder="Brief project description..." />
            </div>
            <button className={css.btnPrimary} onClick={() => setStep(2)} disabled={!form.name}>Next: Customer Details</button>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <h3 style={{ fontSize: 'var(--text-lg)' }}>Customer Details</h3>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Customer Name</label>
                <input className={css.formInput} value={form.customer_name} onChange={e => update('customer_name', e.target.value)} />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Company</label>
                <input className={css.formInput} value={form.customer_company} onChange={e => update('customer_company', e.target.value)} />
              </div>
            </div>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Email</label>
                <input className={css.formInput} type="email" value={form.customer_email} onChange={e => update('customer_email', e.target.value)} />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Phone</label>
                <input className={css.formInput} value={form.customer_phone} onChange={e => update('customer_phone', e.target.value)} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button className={css.btnSecondary} onClick={() => setStep(1)}>Back</button>
              <button className={css.btnPrimary} onClick={() => setStep(3)}>Next: Site Info</button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <h3 style={{ fontSize: 'var(--text-lg)' }}>Site Information</h3>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Address</label>
              <input className={css.formInput} value={form.site_line1} onChange={e => update('site_line1', e.target.value)} placeholder="Street address" />
            </div>
            <div className={css.formRow}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>City</label>
                <input className={css.formInput} value={form.site_city} onChange={e => update('site_city', e.target.value)} />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Postal Code</label>
                <input className={css.formInput} value={form.site_postal_code} onChange={e => update('site_postal_code', e.target.value)} />
              </div>
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Country</label>
              <input className={css.formInput} value={form.site_country} onChange={e => update('site_country', e.target.value)} placeholder="e.g. Spain" />
            </div>
            <div className={css.formGroup}>
              <label className={css.formLabel}>Site Notes</label>
              <textarea className={css.formTextarea} value={form.site_notes} onChange={e => update('site_notes', e.target.value)} placeholder="Access instructions, special conditions..." />
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button className={css.btnSecondary} onClick={() => setStep(2)}>Back</button>
              <button className={css.btnPrimary} onClick={handleSubmit} disabled={loading}>
                {loading ? 'Creating...' : 'Create Project'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
